"use client"

import React from "react"
import { log } from '@/lib/logger'
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { ContactAvatar } from "@/components/conversations/contact-avatar"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { format, parseISO } from "date-fns"
import { Store, DollarSign, Users2, Building2, Briefcase, Plus, X, Save, MapPin, ExternalLink, Ruler, Bed, Bath, Euro, Link2, CheckCircle2, Clock } from "lucide-react"
import { TagSelector } from "@/components/ui/tag-selector"
import type { Tag as UITag } from "@/components/ui/tag-selector"
import type { Mail } from "@/lib/data"
import { transformConversationsToMails } from "@/lib/conversation-utils"
import {
  betterplaceService,
  type CombinedPropertyLeads,
} from "@/services/betterplaceService"
import {
  conversationsApi,
  type Conversation as ApiConversation,
} from "@/services/conversationsApi"
// import { type Conversation } from "@/services/conversationsApi" // Commented out, not used after moving intentions to calls tab
import { tagService } from "@/services/TagService"
import { WhatsAppTemplateSelector } from "./whatsapp-template-selector"
import { TagSuggestions } from './TagSuggestions'
import { COLOR_DE_CANAL } from '@/lib/colores-de-canal'
import { cn } from "@/lib/utils"

interface UserCategoryResponse {
  user_id: string
  user_category: string
  category_display: string
  message?: string
}

interface UserTagsResponse {
  tags: UITag[]
}

type UserTagPolicyMode = "manual_protegido" | "adaptativo"

// interface IntentData {
//   intent_type?: string
//   intent_confidence?: number
// }

interface UserProfileProps {
  mail: Mail | null
  translations?: (key: string) => string
  isPropertyLead?: boolean
  onOpenConversation?: (conversationId: string) => void
}

interface InterestedClient {
  id: string
  name: string
  email: string
  phone: string
  notes: string
}

interface PropertyOwner {
  name: string
  email: string
  phone: string
}

interface PropertyData {
  title: string
  price: number
  platform: string
  platformUrl: string
  zone: string
  image: string
  area: number
  rooms: number
  bathrooms: number
  crossStatus: string
  description: string
  owner: PropertyOwner
  interestedClients: InterestedClient[]
}

function formatBetterplaceZone(data: CombinedPropertyLeads['property']): string {
  const location = data.location
  const parts = [location?.city, location?.province].filter(
    (value): value is string => typeof value === 'string' && value.trim().length > 0,
  )

  if (parts.length > 0) {
    return parts.join(' · ')
  }

  const address = typeof location?.address === 'string' ? location.address.trim() : ''
  return address || 'Betterplace'
}

function buildBetterplacePropertyData(mail: Mail, combined: CombinedPropertyLeads): PropertyData {
  const property = combined.property
  const propertyDetails = property.propertyDetails
  const image = property.images?.[0] || 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=800'

  return {
    title: property.title || mail.subject || 'Cruce Betterplace',
    price: Number(property.price || 0),
    platform: 'Betterplace',
    platformUrl: property.url || 'https://rkcompradores.alt-94.dev/',
    zone: formatBetterplaceZone(property),
    image,
    area: Number(propertyDetails?.surfaceAreaSqm || 0),
    rooms: Number(propertyDetails?.bedrooms || 0),
    bathrooms: Number(propertyDetails?.bathrooms || 0),
    crossStatus: combined.expired ? 'Expirado' : 'Activo',
    description: property.description || 'Cruce Betterplace sincronizado desde el productor.',
    owner: {
      name: combined.betterplaceSellerLead?.name || mail.name || 'Lead Betterplace',
      email: mail.email || '',
      phone: combined.betterplaceSellerLead?.phone || mail.phone || '',
    },
    interestedClients: combined.interestedClients.map((client, index) => ({
      id: `${combined.crossId || mail.propertyId || mail.id}-client-${index + 1}`,
      name: client.name || `Comprador ${index + 1}`,
      email: '',
      phone: client.phone || '',
      notes: 'Comprador interesado sincronizado desde Betterplace.',
    })),
  }
}

function getFallbackBetterplacePropertyData(mail: Mail): PropertyData {
  return {
    title: mail.subject || 'Cruce Betterplace',
    price: 0,
    platform: 'Betterplace',
    platformUrl: 'https://rkcompradores.alt-94.dev/',
    zone: 'Betterplace',
    image: 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=800',
    area: 0,
    rooms: 0,
    bathrooms: 0,
    crossStatus: 'Activo',
    description: 'No se pudo cargar el detalle completo del cruce Betterplace.',
    owner: {
      name: mail.name || 'Lead Betterplace',
      email: mail.email || '',
      phone: mail.phone || '',
    },
    interestedClients: [],
  }
}

function normalizePhoneForMatch(value: string | null | undefined): string {
  if (!value) {
    return ''
  }

  const trimmed = value.trim()
  if (!trimmed) {
    return ''
  }

  const hasPlus = trimmed.startsWith('+')
  const digits = trimmed.replace(/\D/g, '')
  if (!digits) {
    return ''
  }

  return hasPlus ? `+${digits}` : digits
}

function getConversationPhoneCandidates(conversation: ApiConversation): string[] {
  const client = conversation.client && typeof conversation.client === 'object'
    ? conversation.client as { phone?: string | null }
    : undefined

  return [client?.phone, conversation.channel_user_id]
    .map((value) => normalizePhoneForMatch(value || ''))
    .filter(Boolean)
}


// Platform icons
const PLATFORM_ICONS = {
  whatsapp: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke={COLOR_DE_CANAL.whatsapp}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
    >
      <path d="M3 21l1.9-5.7a8.5 8.5 0 113.8 3.8z" />
    </svg>
  ),
  instagram: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke={COLOR_DE_CANAL.instagram}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
    >
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>
  ),
  facebook: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke={COLOR_DE_CANAL.facebook}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
    >
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
    </svg>
  ),
  email: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
    >
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  web: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  ),
  default: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  ),
}


const CATEGORY_ICONS = {
  client: Store,
  lead: DollarSign,
  collaborator: Users2,
  employee: Building2,
  vendor: Briefcase,
}

export function UserProfile({ mail, translations, isPropertyLead = false, onOpenConversation }: UserProfileProps) {
  // Translation helper function
  const t = (key: string) => {
    if (!translations) return key
    return translations(key)
  }


  // Real data from API
  const [availableTags, setAvailableTags] = React.useState<UITag[]>([])
  const [userTags, setUserTags] = React.useState<UITag[]>([])
  const [isLoadingTags, setIsLoadingTags] = React.useState(false)
  const [userTagPolicy, setUserTagPolicy] = React.useState<UserTagPolicyMode>("manual_protegido")

  const [userCategory, setUserCategory] = React.useState<string>("")
  const [categoryDisplay, setCategoryDisplay] = React.useState<string>("")
  const [isLoadingCategory, setIsLoadingCategory] = React.useState(false)

  // User intentions state - Only for phone calls in Profile
  const [userIntentions, setUserIntentions] = React.useState<Array<{
    id: string
    intent: string
    confidence: number
    timestamp: string
  }>>([])
  const [isLoadingIntentions, setIsLoadingIntentions] = React.useState(false)
  const availableCategories = React.useMemo(() => [
    { value: "lead", label: "Lead" },
    { value: "client", label: "Client" },
    { value: "collaborator", label: "Collaborator" },
    { value: "employee", label: "Employee" },
    { value: "vendor", label: "Vendor" },
  ], [])

  const [selectedTags, setSelectedTags] = React.useState<string[]>([])
  const [isDialogOpen, setIsDialogOpen] = React.useState(false)
  const [isAddingExistingTags, setIsAddingExistingTags] = React.useState(false)
  const [dialogSelectedUiTags, setDialogSelectedUiTags] = React.useState<UITag[]>([])

  React.useEffect(() => {
    // Initialize dialog selected tags with current user tags when opening
    if (isDialogOpen) {
      const initialUi = availableTags.filter(t => selectedTags.includes(t.id)) as UITag[]
      setDialogSelectedUiTags(initialUi)
    }
  }, [isDialogOpen, selectedTags, availableTags])

  React.useEffect(() => {
    // Sync selectedTags with userTags from API
    if (userTags) {
      setSelectedTags(userTags.map(tag => tag.id))
    }
  }, [userTags])

  // Load user category and tags when mail changes
  React.useEffect(() => {
    const loadUserData = async () => {
      if (!mail?.user) {
        log.debug('No user ID available for data loading')
        return
      }

      // Check if this is a phone call user
      const isPhoneUser = mail?.platform === 'phone_call'
      log.debug('🔍 Loading data for user', { userId: mail.user, isPhoneUser })

      // Load user category
      setIsLoadingCategory(true)
      try {
        let response: UserCategoryResponse

        if (isPhoneUser) {
          // Use phone user endpoint
          response = await conversationsApi.getPhoneUserCategory(mail.user) as UserCategoryResponse
        } else {
          // Use regular user endpoint
          response = await conversationsApi.getUserCategory(mail.user) as UserCategoryResponse
        }

        if (response?.user_category) {
          setUserCategory(response.user_category)
          setCategoryDisplay(response.category_display || response.user_category)
          log.debug('✅ Loaded user category', { userCategory: response.user_category, categoryDisplay: response.category_display })
        } else {
          log.debug('⚠️ No category found for user, using default')
          setUserCategory("lead")
          setCategoryDisplay("Lead")
        }
      } catch (error) {
        console.error('❌ Failed to load user category:', error)
        // Use default category on error
        setUserCategory("lead")
        setCategoryDisplay("Lead")
      } finally {
        setIsLoadingCategory(false)
      }

      // Load available tags and user tags
      setIsLoadingTags(true)
      try {
        log.debug('🔍 Loading tags for user:', mail.user)

        // Load available tags (all tags)
        const allTagsResponse = await tagService.getTags()
        setAvailableTags(Array.isArray(allTagsResponse) ? allTagsResponse : [])

        // Load user's current tags
        let userTagsResponse: UITag[]
        if (isPhoneUser) {
          // Use phone user endpoint
          const response = await conversationsApi.getPhoneUserTags(mail.user) as UserTagsResponse
          userTagsResponse = response?.tags || []
        } else {
          // Use regular user endpoint
          userTagsResponse = await conversationsApi.getUserTags(mail.user) as UITag[]
        }

        setUserTags(Array.isArray(userTagsResponse) ? userTagsResponse : [])

        const policy = await conversationsApi.getGlobalTagPolicy() as { policy_mode?: UserTagPolicyMode }
        if (policy?.policy_mode === "adaptativo" || policy?.policy_mode === "manual_protegido") {
          setUserTagPolicy(policy.policy_mode)
        } else {
          setUserTagPolicy("manual_protegido")
        }

        log.debug('✅ Loaded tags', {
          availableCount: Array.isArray(allTagsResponse) ? allTagsResponse.length : 0,
          userCount: Array.isArray(userTagsResponse) ? userTagsResponse.length : 0,
        })
      } catch (error) {
        console.error('❌ Failed to load tags:', error)
        setAvailableTags([])
        setUserTags([])
        setUserTagPolicy("manual_protegido")
      } finally {
        setIsLoadingTags(false)
      }

      // Load user intentions for phone calls only
      if (isPhoneUser) {
        setIsLoadingIntentions(true)
        // For phone calls, use the call description/summary as the main intent
        const callText = mail?.text || mail?.subject || ''
        if (callText.trim()) {
          // Get a valid date - try multiple sources
          let validDate = new Date().toISOString()
          if (mail?.date) {
            const dateObj = new Date(mail.date)
            if (!isNaN(dateObj.getTime())) {
              validDate = dateObj.toISOString()
            }
          } else if (mail?.displayDate) {
            // If displayDate is a relative string like "Hace 2 horas", use current date
            validDate = new Date().toISOString()
          }

          setUserIntentions([
            {
              id: 'call-description',
              intent: callText.trim(),
              confidence: 0.95, // High confidence since it's from ElevenLabs analysis
              timestamp: validDate
            }
          ])
        } else {
          setUserIntentions([])
        }
        setIsLoadingIntentions(false)
      }
      // if (!isPhoneUser) {
      //   setIsLoadingIntentions(true)
      //   try {
      //     log.debug('🔍 Loading user intentions for:', mail.user)

      //     // Get user's conversations to extract intentions
      //     const conversationsResponse = await conversationsApi.getConversationsByUser(mail.user)
      //     const conversations = conversationsResponse?.results || []

      //     // Extract intentions from conversation metadata
      //     const intentions: Array<{
      //       id: string
      //       intent: string
      //       confidence: number
      //       timestamp: string
      //     }> = []

      //     conversations.forEach((conversation: Conversation) => {
      //       if (conversation.metadata?.intent_status && conversation.metadata.intent_status !== 'analyzing') {
      //         // Extract intent information from metadata
      //         const intentData = (conversation.metadata.intent_data || {}) as IntentData
      //         if (intentData.intent_type) {
      //           intentions.push({
      //             id: conversation.id,
      //             intent: intentData.intent_type,
      //             confidence: intentData.intent_confidence || 0,
      //             timestamp: conversation.started_at
      //           })
      //         }
      //       }
      //     })

      //     setUserIntentions(intentions)
      //     log.debug('✅ Loaded user intentions:', intentions.length)
      //   } catch (error) {
      //     console.error('❌ Failed to load user intentions:', error)
      //     setUserIntentions([])
      //   } finally {
      //     setIsLoadingIntentions(false)
      //   }
      // } else {
      //   // For phone calls, use the call description as the main intent
      //   if (mail.text && mail.text.trim()) {
      //     setUserIntentions([
      //       {
      //         id: 'call-description',
      //         intent: mail.text.trim(),
      //         confidence: 0.95, // High confidence since it's from ElevenLabs analysis
      //         timestamp: mail.date
      //       }
      //     ])
      //   } else {
      //     setUserIntentions([])
      //   }
      // }
    }

    loadUserData()
  }, [mail?.user, mail?.platform, mail?.text, mail?.subject, mail?.date, mail?.displayDate])

  const handleDialogClose = () => {
    setIsDialogOpen(false)
    setDialogSelectedUiTags([])
    setIsAddingExistingTags(false)
  }

  const handleSaveSelectedTags = async () => {
    if (!mail?.user) {
      console.error('No user ID available for saving tags')
      return
    }

    try {
      const ids = dialogSelectedUiTags.map(t => t.id)
      const isPhoneUser = mail?.platform === 'phone_call'
      log.debug('🚀 Saving user tags', { ids, isPhoneUser })

      // Update tags in database
      let updatedTags: UITag[]
      if (isPhoneUser) {
        // Use phone user endpoint
        const response = await conversationsApi.updatePhoneUserTags(mail.user, ids) as UserTagsResponse
        updatedTags = response?.tags || []
      } else {
        // Use regular user endpoint
        updatedTags = await conversationsApi.updateUserTags(mail.user, ids) as UITag[]
      }

      // Update local state
      setSelectedTags(ids)
      setUserTags(Array.isArray(updatedTags) ? updatedTags : [])

      log.debug('✅ Tags saved successfully:', Array.isArray(updatedTags) ? updatedTags.length : 0)

      setIsDialogOpen(false)
      setDialogSelectedUiTags([])
      setIsAddingExistingTags(false)
    } catch (error) {
      console.error('❌ Failed to save tags:', error)
    }
  }

  const removeTag = async (tagId: string) => {
    if (!mail?.user) {
      console.error('No user ID available for removing tag')
      return
    }

    try {
      const updatedTagIds = selectedTags.filter((id) => id !== tagId)
      const isPhoneUser = mail?.platform === 'phone_call'
      log.debug('🚀 Removing tag', { tagId, updatedTagIds, isPhoneUser })

      // Update tags in database
      let updatedTags: UITag[]
      if (isPhoneUser) {
        // Use phone user endpoint
        const response = await conversationsApi.updatePhoneUserTags(mail.user, updatedTagIds) as UserTagsResponse
        updatedTags = response?.tags || []
      } else {
        // Use regular user endpoint
        updatedTags = await conversationsApi.updateUserTags(mail.user, updatedTagIds) as UITag[]
      }

      // Update local state
      setSelectedTags(updatedTagIds)
      setUserTags(Array.isArray(updatedTags) ? updatedTags : [])

      log.debug('✅ Tag removed successfully')
    } catch (error) {
      console.error('❌ Failed to remove tag:', error)
    }
  }

  const handleCategoryChange = async (category: string) => {
    try {
      log.debug('🔄 Category changed:', category)
      log.debug('📧 Mail object:', mail)
      log.debug('👤 Mail user ID:', mail?.user)

      // Check if this is a phone call user
      const isPhoneUser = mail?.platform === 'phone_call'

      // Find the category label
      const selectedCategory = availableCategories.find(cat => cat.value === category)
      const newDisplay = selectedCategory ? selectedCategory.label : category

      // Update the category state optimistically
      setUserCategory(category)
      setCategoryDisplay(newDisplay)

      // Update category in database
      if (mail?.user) {
        log.debug('🚀 Calling API to update user category', {
          userId: mail.user,
          category,
          isPhoneUser,
        })

        if (isPhoneUser) {
          // Use phone user endpoint
          await conversationsApi.updatePhoneUserCategory(mail.user, category)
        } else {
          // Use regular user endpoint
          await conversationsApi.updateUserCategory(mail.user, category)
        }

        log.debug('✅ Category updated in database', { category, display: newDisplay })
      } else {
        log.debug('❌ No user ID available for category update')
        log.debug('📧 Available mail properties:', Object.keys(mail || {}))
      }
    } catch (error) {
      console.error('❌ Failed to update category:', error)
      // Revert optimistic update on error
      const originalCategory = availableCategories.find(cat => cat.value === userCategory)
      setCategoryDisplay(originalCategory ? originalCategory.label : userCategory)
    }
  }

  if (!mail) {
    return <div className="p-8 text-center text-muted-foreground">No user selected</div>
  }

  // If this is a property lead, show property profile
  if (isPropertyLead) {
    return (
      <PropertyProfile
        mail={mail}
        translations={translations}
        onOpenConversation={onOpenConversation}
      />
    )
  }

  const messageDate = parseISO(mail.date)

  // Get platform icon
  const platformIcon = PLATFORM_ICONS[mail.platform.toLowerCase() as keyof typeof PLATFORM_ICONS] || PLATFORM_ICONS.default

  return (
    <ScrollArea className="h-full">
      <div className="space-y-8 p-6">
        <div className="flex items-center space-x-4">
          <ContactAvatar
            className="h-20 w-20"
            profilePicUrl={mail.profilePicUrl}
            email={mail.email}
            userId={mail.user}
            displayName={mail.name}
          />
          <div className="min-w-0 space-y-2">
            <h2 className="break-words text-2xl font-bold">{mail.name}</h2>
            <p className="truncate text-muted-foreground" title={mail.email}>{mail.email}</p>
            {mail.phone && <p className="truncate text-muted-foreground">{mail.phone}</p>}
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{t('user_profile.classification')}</CardTitle>
            <CardDescription>{t('user_profile.classify_and_organize_contact')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  {t('user_profile.category')}
                </label>
              </div>
              <Select
                value={userCategory || 'lead'}
                onValueChange={(value) => handleCategoryChange(value)}
                disabled={isLoadingCategory}
              >
                <SelectTrigger className="w-full bg-background">
                  <div className="flex items-center gap-2">
                    {userCategory && CATEGORY_ICONS[userCategory as keyof typeof CATEGORY_ICONS] &&
                      React.createElement(CATEGORY_ICONS[userCategory as keyof typeof CATEGORY_ICONS], { className: "h-4 w-4" })}
                    <span>{isLoadingCategory ? 'Cargando...' : (categoryDisplay || 'Sin categoría')}</span>
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {availableCategories.map((category) => (
                    <SelectItem key={category.value} value={category.value} className="!cursor-pointer">
                      <div className="flex items-center gap-2">
                        {React.createElement(CATEGORY_ICONS[category.value as keyof typeof CATEGORY_ICONS], { className: "h-4 w-4" })}
                        <span className="font-medium">{category.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  {t('user_profile.tags')}
                </label>
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="xs">
                      <Plus className="mr-1 h-4 w-4" />
                      {t('user_profile.add_tag')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle>{t('user_profile.add_tag')}</DialogTitle>
                      <DialogDescription>
                        {t('user_profile.add_tags_description')}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <p className="text-xs leading-relaxed text-muted-foreground">
                        {t('user_profile.semantic_hint')}
                      </p>
                      <TagSelector
                        availableTags={availableTags}
                        selectedTags={dialogSelectedUiTags}
                        onTagsChange={(tags) => {
                          setDialogSelectedUiTags(tags)
                          setIsAddingExistingTags(true)
                        }}
                        placeholder={t('user_profile.select_tags_placeholder')}
                        allowCreate={false}
                        size="md"
                      />
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={handleDialogClose}>
                        {t('common.cancel')}
                      </Button>
                      <Button onClick={handleSaveSelectedTags} disabled={!isAddingExistingTags}>
                        <Save className="mr-2 h-4 w-4" />
                        {t('user_profile.save_tags')}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>

              {mail?.id && mail.platform !== 'phone_call' ? (
                <TagSuggestions
                  conversationId={mail.id}
                  onConfirmed={() => {
                    void conversationsApi
                      .getUserTags(mail.user)
                      .then((tags) => setUserTags(Array.isArray(tags) ? (tags as UITag[]) : []))
                  }}
                />
              ) : null}
              <div className="space-y-1 rounded-md border bg-muted/10 px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-medium text-muted-foreground">{t('user_profile.policy_label')}</span>
                  <span className="rounded-md bg-muted px-2 py-0.5 text-xs font-medium">
                    {userTagPolicy === "adaptativo"
                      ? t('user_profile.policy_mode_adaptive')
                      : t('user_profile.policy_mode_strict')}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {userTagPolicy === "adaptativo"
                    ? t('user_profile.policy_description_adaptive')
                    : t('user_profile.policy_description_strict')}
                  {" "}Administrada globalmente desde Settings.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {isLoadingTags ? (
                  <div className="text-sm text-muted-foreground">Loading tags...</div>
                ) : userTags.length > 0 ? (
                  userTags.map((tag) => (
                    <Badge
                      key={tag.id}
                      variant="secondary"
                      className="flex cursor-pointer items-center gap-1 hover:bg-destructive/10 hover:text-destructive"
                      onClick={() => removeTag(tag.id)}
                    >
                      <div
                        className="h-2 w-2 rounded-full"
                        style={{ backgroundColor: tag.color }}
                      />
                      {tag.name}
                      <X className="h-3 w-3" />
                    </Badge>
                  ))
                ) : (
                  <div className="text-sm text-muted-foreground">{t('user_profile.no_tags_assigned')}</div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('user_profile.user_information')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div>
              <span className="font-semibold">{t('user_profile.first_contact')}:</span> {format(messageDate, "dd/MM/yyyy")}
            </div>
            <div className="space-y-1">
              <span className="font-semibold">{t('user_profile.platforms')}:</span>
              <div className="mt-1 flex items-center gap-2">
                {platformIcon}
                <span className="capitalize">{mail.platform}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* WhatsApp Templates section - Only for WhatsApp conversations */}
        {mail?.platform?.toLowerCase() === 'whatsapp' && mail?.user && (
          <WhatsAppTemplateSelector userId={mail.user} />
        )}

        {/* User Intentions section - Only for phone calls */}
        {mail?.platform === 'phone_call' && (
          <Card>
            <CardHeader>
              <CardTitle>{t('user_profile.user_intentions')}</CardTitle>
              <CardDescription>{t('user_profile.analyzed_intents_from_user_interactions')}</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingIntentions ? (
                <div className="text-sm text-muted-foreground">{t('user_profile.loading_intentions')}</div>
              ) : userIntentions.length > 0 ? (
                <ul className="space-y-2">
                  {userIntentions.map((intention) => {
                    // Parse date safely to avoid "Invalid Date"
                    let dateDisplay = 'Fecha no disponible'
                    try {
                      const dateObj = new Date(intention.timestamp)
                      if (!isNaN(dateObj.getTime())) {
                        dateDisplay = dateObj.toLocaleDateString()
                      }
                    } catch {
                      // If date parsing fails, use fallback
                      dateDisplay = 'Fecha no disponible'
                    }

                    return (
                      <li key={intention.id}>
                        <div className="flex flex-col">
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{intention.intent}</span>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {dateDisplay}
                          </span>
                        </div>
                      </li>
                    )
                  })}
                </ul>
              ) : (
                <div className="text-sm text-muted-foreground">
                  {t('user_profile.no_intentions_phone_calls') || 'No se encontraron intenciones para esta llamada'}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Coordinated meetings - Commented out for now, hardcoded data. Will be implemented properly later */}
      </div>
    </ScrollArea>
  )
}

// Mock property data - this would come from the API in production
const getMockPropertyData = (mail: Mail | null) => {
  if (!mail) return null

  // Generate property data based on mail ID or use default
  const propertyId = mail.propertyId || mail.id || 'property-001'

  const properties: Record<string, PropertyData> = {
    'conv-lead-vendor-001': {
      title: 'Chalet moderno con piscina en Gijón',
      price: 485000,
      platform: 'Idealista',
      platformUrl: 'https://www.idealista.com/inmueble/12345678/',
      zone: 'Gijón · Cimadevilla',
      image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=800',
      area: 280,
      rooms: 3,
      bathrooms: 2,
      crossStatus: 'Activo',
      description: 'Hermoso chalet independiente con jardín privado y piscina. Orientación sur, reformado en 2020. Zona residencial tranquila, cerca de playas y servicios.',
      owner: {
        name: mail.name || 'Elena Ruiz',
        email: mail.email,
        phone: mail.phone || '+34 612 345 678'
      },
      interestedClients: [
        { id: 'client-1', name: 'María González', email: 'maria.gonzalez@email.com', phone: '+34 611 111 111', notes: 'Buscando en Gijón, presupuesto hasta 500k' },
        { id: 'client-2', name: 'Carlos Martínez', email: 'carlos.martinez@email.com', phone: '+34 622 222 222', notes: 'Interesado en propiedades con piscina' },
        { id: 'client-3', name: 'Ana Fernández', email: 'ana.fernandez@email.com', phone: '+34 633 333 333', notes: 'Primera vivienda, zona cerca del mar' }
      ]
    },
    'conv-lead-vendor-002': {
      title: 'Edificio comercial de oficinas en Oviedo centro',
      price: 1200000,
      platform: 'Fotocasa',
      platformUrl: 'https://www.fotocasa.es/es/comercial/oviedo/centro/12345678',
      zone: 'Oviedo · Centro',
      image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800',
      area: 850,
      rooms: 0,
      bathrooms: 4,
      crossStatus: 'Expirado',
      description: 'Edificio de oficinas completamente rehabilitado en 2022. Planta baja comercial, 3 plantas superiores con oficinas. Ubicación estratégica en el centro de Oviedo.',
      owner: {
        name: mail.name || 'Roberto Sánchez',
        email: mail.email,
        phone: mail.phone || '+34 612 345 679'
      },
      interestedClients: [
        { id: 'client-4', name: 'Pedro López', email: 'pedro.lopez@email.com', phone: '+34 644 444 444', notes: 'Empresa en expansión, busca espacio comercial' },
        { id: 'client-5', name: 'Laura Díaz', email: 'laura.diaz@email.com', phone: '+34 655 555 555', notes: 'Inversión inmobiliaria, busca rentabilidad' }
      ]
    },
    'conv-lead-vendor-003': {
      title: 'Terreno industrial para proyecto logístico',
      price: 950000,
      platform: 'Idealista',
      platformUrl: 'https://www.idealista.com/inmueble/87654321/',
      zone: 'Avilés · Zona Industrial',
      image: 'https://images.unsplash.com/photo-1472851294608-062f824d29cc?w=800',
      area: 5000,
      rooms: 0,
      bathrooms: 0,
      crossStatus: 'Activo',
      description: 'Terreno industrial de 5000m² con licencia de construcción. Perfecto para proyecto logístico o almacén. Conexión directa a carreteras principales.',
      owner: {
        name: mail.name || 'Isabel Morales',
        email: mail.email,
        phone: mail.phone || '+34 612 345 680'
      },
      interestedClients: [
        { id: 'client-6', name: 'Javier Torres', email: 'javier.torres@email.com', phone: '+34 666 666 666', notes: 'Empresa de distribución, busca terreno industrial' }
      ]
    },
    'conv-lead-vendor-004': {
      title: 'Piso reformado en Oviedo centro',
      price: 185000,
      platform: 'Idealista',
      platformUrl: 'https://www.idealista.com/inmueble/11111111/',
      zone: 'Oviedo · Centro',
      image: 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=800',
      area: 85,
      rooms: 2,
      bathrooms: 1,
      crossStatus: 'Expirado',
      description: 'Piso completamente reformado en 2023. Ubicado en el corazón de Oviedo, cerca de todos los servicios. Orientación sur, muy luminoso.',
      owner: {
        name: mail.name || 'Antonio Jiménez',
        email: mail.email,
        phone: mail.phone || '+34 612 345 681'
      },
      interestedClients: [
        { id: 'client-7', name: 'Lucía Pérez', email: 'lucia.perez@email.com', phone: '+34 677 777 777', notes: 'Joven profesional buscando primera vivienda en centro' },
        { id: 'client-8', name: 'Miguel Ángel Ruiz', email: 'miguel.ruiz@email.com', phone: '+34 688 888 888', notes: 'Inversor buscando propiedades reformadas en Oviedo' }
      ]
    },
    'conv-lead-vendor-005': {
      title: 'Casa unifamiliar con jardín en Gijón',
      price: 320000,
      platform: 'Fotocasa',
      platformUrl: 'https://www.fotocasa.es/es/vivienda/gijon/la-ería/22222222',
      zone: 'Gijón · La Ería',
      image: 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=800',
      area: 180,
      rooms: 4,
      bathrooms: 3,
      crossStatus: 'Activo',
      description: 'Encantadora casa unifamiliar de 4 habitaciones con amplio jardín. Reformada en 2021. Zona tranquila y familiar, perfecta para familias.',
      owner: {
        name: mail.name || 'Carmen Delgado',
        email: mail.email,
        phone: mail.phone || '+34 612 345 682'
      },
      interestedClients: [
        { id: 'client-9', name: 'Sofía Ramírez', email: 'sofia.ramirez@email.com', phone: '+34 699 999 999', notes: 'Familia con dos hijos buscando casa con jardín' },
        { id: 'client-10', name: 'Andrés Moreno', email: 'andres.moreno@email.com', phone: '+34 610 000 000', notes: 'Buscando vivienda familiar en La Ería, presupuesto hasta 350k' }
      ]
    }
  }

  // Default fallback property data
  const defaultProperty = {
    title: 'Propiedad en venta',
    price: 250000,
    platform: 'Idealista',
    platformUrl: 'https://www.idealista.com/',
    zone: 'Zona no especificada',
    image: 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=800',
    area: 100,
    rooms: 3,
    bathrooms: 2,
    crossStatus: 'Activo',
    description: 'Propiedad disponible en nuestro catálogo.',
    owner: {
      name: mail.name || 'Propietario',
      email: mail.email || '',
      phone: mail.phone || ''
    },
    interestedClients: []
  }

  return properties[propertyId] || defaultProperty
}

// Property Profile Component
function PropertyProfile({
  mail,
  onOpenConversation,
}: {
  mail: Mail | null
  translations?: (key: string) => string
  onOpenConversation?: (conversationId: string) => void
}) {
  const shouldLoadBetterplace = Boolean(mail && mail.platform === 'betterplace' && mail.propertyId)
  const [betterplacePropertyData, setBetterplacePropertyData] = React.useState<PropertyData | null>(null)
  const [betterplacePropertyError, setBetterplacePropertyError] = React.useState<string | null>(null)
  const [betterplacePropertyLoading, setBetterplacePropertyLoading] = React.useState(false)
  const [relatedWhatsappConversations, setRelatedWhatsappConversations] = React.useState<Mail[]>([])
  const [relatedWhatsappLoading, setRelatedWhatsappLoading] = React.useState(false)
  const [relatedWhatsappError, setRelatedWhatsappError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!mail || mail.platform !== 'betterplace' || !mail.propertyId) {
      setBetterplacePropertyData(null)
      setBetterplacePropertyError(null)
      setBetterplacePropertyLoading(false)
      setRelatedWhatsappConversations([])
      setRelatedWhatsappError(null)
      setRelatedWhatsappLoading(false)
      return
    }

    let cancelled = false

    // La guarda de arriba ya descarta el propertyId vacio, pero TypeScript
    // pierde ese estrechamiento dentro de la funcion anidada. Capturarlo aqui
    // conserva la comprobacion en vez de silenciarla con un `!`.
    const propertyId = mail.propertyId

    const loadBetterplaceProperty = async () => {
      setBetterplacePropertyLoading(true)
      setBetterplacePropertyError(null)
      setRelatedWhatsappLoading(true)
      setRelatedWhatsappError(null)

      try {
        const combined = await betterplaceService.getCombinedPropertyLeads(propertyId)
        if (cancelled) return

        setBetterplacePropertyData(buildBetterplacePropertyData(mail, combined))

        const targetPhones = new Set(
          combined.interestedClients
            .map((client) => normalizePhoneForMatch(client.phone))
            .filter(Boolean),
        )

        if (targetPhones.size === 0) {
          setRelatedWhatsappConversations([])
        } else {
          const response = await conversationsApi.listConversations({
            source: 'WhatsApp',
            page_size: 100,
            // Todos los hilos, no solo la cabeza de cada cliente: aqui se
            // buscan por telefono y agrupar escondería los de otras lineas.
            threads: 'all',
          })
          if (cancelled) return

          const conversations = Array.isArray(response.results) ? response.results : []
          const relatedConversations = conversations.filter((conversation) => {
            const phoneCandidates = getConversationPhoneCandidates(conversation)
            return phoneCandidates.some((phoneNumber) => targetPhones.has(phoneNumber))
          })

          setRelatedWhatsappConversations(transformConversationsToMails(relatedConversations))
        }
      } catch (error) {
        if (cancelled) return

        log.warn('Failed to load Betterplace property profile', {
          propertyId: mail.propertyId,
          error: error instanceof Error ? error.message : String(error),
        })
        setBetterplacePropertyData(getFallbackBetterplacePropertyData(mail))
        setBetterplacePropertyError(
          error instanceof Error ? error.message : 'No se pudo cargar el detalle Betterplace.',
        )
        setRelatedWhatsappConversations([])
        setRelatedWhatsappError(
          error instanceof Error ? error.message : 'No se pudieron cargar las conversaciones WhatsApp relacionadas.',
        )
      } finally {
        if (!cancelled) {
          setBetterplacePropertyLoading(false)
          setRelatedWhatsappLoading(false)
        }
      }
    }

    void loadBetterplaceProperty()

    return () => {
      cancelled = true
    }
  }, [mail])

  const propertyData = shouldLoadBetterplace
    ? betterplacePropertyData
    : getMockPropertyData(mail)

  if (!mail || !propertyData) {
    return <div className="p-8 text-center text-muted-foreground">No property selected</div>
  }

  // Activo o expirado: dos estados, un sitio. `success` y `warning` ya llevan
  // su version oscura dentro, asi que aqui no hay que escribir ninguna.
  const cruce =
    propertyData.crossStatus === 'Activo'
      ? {
          Icono: CheckCircle2,
          insignia: 'border-success/30 bg-success text-success-foreground',
          tarjeta: 'border-success/20 bg-success/10',
          fondo: 'bg-success/20',
          texto: 'text-success',
        }
      : {
          Icono: Clock,
          insignia: 'border-warning/30 bg-warning text-warning-foreground',
          tarjeta: 'border-warning/20 bg-warning/10',
          fondo: 'bg-warning/20',
          texto: 'text-warning',
        }

  return (
    <ScrollArea className="h-full">
      <div className="space-y-8 p-6">
        {shouldLoadBetterplace && (betterplacePropertyLoading || betterplacePropertyError) && (
          <Card className={betterplacePropertyError ? 'border-warning/40 bg-warning/5' : 'border-primary/30 bg-primary/5'}>
            <CardContent className="p-4 text-sm text-muted-foreground">
              {betterplacePropertyLoading
                ? 'Cargando detalle Betterplace...'
                : `Detalle Betterplace parcialmente disponible: ${betterplacePropertyError}`}
            </CardContent>
          </Card>
        )}

        {/* Property Image and Main Info */}
        <Card className="overflow-hidden">
          <div className="relative h-64 w-full">
            <img
              src={propertyData.image}
              alt={propertyData.title}
              className="w-full h-full object-cover"
              onError={(e) => { if (!e.currentTarget.src.endsWith('/logo.png')) e.currentTarget.src = '/logo.png' }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4">
              <div className="flex items-start justify-between gap-4 mb-2">
                <h1 className="text-2xl font-bold text-white flex-1">
                  {propertyData.title}
                </h1>
                {/* Cross Status Badge */}
                {propertyData.crossStatus && (
                  <div className={cn('flex items-center gap-2 rounded-full border px-4 py-2 shadow-md', cruce.insignia)}>
                    <cruce.Icono className="h-4 w-4" />
                    <span className="text-sm font-semibold">{propertyData.crossStatus}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-4 text-white/90">
                <div className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  <span className="text-sm">{propertyData.zone}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Euro className="h-4 w-4" />
                  <span className="text-lg font-semibold">{propertyData.price.toLocaleString('es-ES')} €</span>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* El estado del cruce, una vez. Estaba escrito siete veces -la misma
            pregunta, con su pareja claro/oscuro cada vez-, y por eso ninguna de
            las siete se podia cambiar sin cambiar las otras seis. */}
        {propertyData.crossStatus && (
          <Card className={cruce.tarjeta}>
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className={cn('rounded-lg p-2', cruce.fondo)}>
                  <cruce.Icono className={cn('h-5 w-5', cruce.texto)} />
                </div>
                <div className="flex-1">
                  <div className={cn('text-sm font-semibold', cruce.texto)}>
                    Estado del cruce: {propertyData.crossStatus}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {propertyData.crossStatus === 'Activo'
                      ? 'Este cruce está activo y disponible para contactar'
                      : 'Este cruce ha expirado y necesita renovación'}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Interested Clients from CRM */}
        <Card>
          <CardHeader>
            <CardTitle>Posibles clientes interesados en CRM</CardTitle>
            <CardDescription>Clientes de nuestro CRM que podrían estar interesados en esta propiedad</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {propertyData.interestedClients.map((client) => (
                <div key={client.id} className="flex items-start gap-4 p-3 rounded-lg border bg-background/50">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={`https://avatar.vercel.sh/${client.email}`} alt={client.name} />
                    <AvatarFallback>
                      {client.name
                        .split(" ")
                        .map((chunk: string) => chunk[0])
                        .join("")}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm">{client.name}</h4>
                    <p className="text-xs text-muted-foreground truncate">{client.email}</p>
                    {client.phone && (
                      <p className="text-xs text-muted-foreground">{client.phone}</p>
                    )}
                    {client.notes && (
                      <p className="text-xs text-muted-foreground mt-1 italic">{client.notes}</p>
                    )}
                  </div>
                </div>
              ))}
              {propertyData.interestedClients.length === 0 && (
                <div className="text-sm text-muted-foreground text-center py-4">
                  No hay clientes interesados registrados aún
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {shouldLoadBetterplace && (
          <Card>
            <CardHeader>
              <CardTitle>Conversaciones WhatsApp relacionadas</CardTitle>
              <CardDescription>
                Conversaciones existentes en Canales asociadas por teléfono con compradores de este cruce.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {relatedWhatsappLoading && (
                  <div className="text-sm text-muted-foreground">Buscando conversaciones WhatsApp relacionadas...</div>
                )}
                {!relatedWhatsappLoading && relatedWhatsappError && (
                  <div className="text-sm text-warning">
                    {relatedWhatsappError}
                  </div>
                )}
                {!relatedWhatsappLoading && !relatedWhatsappError && relatedWhatsappConversations.map((conversation) => (
                  <div key={conversation.id} className="flex items-start justify-between gap-2 rounded-lg border bg-background/50 p-3">
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-medium">{conversation.name}</span>
                        <Badge variant="outline" className="text-[11px] uppercase tracking-wide">
                          {conversation.status}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {conversation.phone || 'Sin teléfono visible'}
                      </div>
                      <div className="line-clamp-2 text-xs text-muted-foreground">
                        {conversation.text}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onOpenConversation?.(conversation.id)}
                      disabled={!onOpenConversation}
                    >
                      Abrir
                    </Button>
                  </div>
                ))}
                {!relatedWhatsappLoading && !relatedWhatsappError && relatedWhatsappConversations.length === 0 && (
                  <div className="text-sm text-muted-foreground text-center py-4">
                    No se encontraron conversaciones WhatsApp relacionadas para los compradores de este cruce.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Property Details */}
        <Card>
          <CardHeader>
            <CardTitle>Detalles de la propiedad</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-2">
                <Ruler className="h-5 w-5 text-muted-foreground" />
                <div>
                  <div className="text-sm font-medium">{propertyData.area} m²</div>
                  <div className="text-xs text-muted-foreground">Superficie</div>
                </div>
              </div>
              {propertyData.rooms > 0 && (
                <div className="flex items-center gap-2">
                  <Bed className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <div className="text-sm font-medium">{propertyData.rooms}</div>
                    <div className="text-xs text-muted-foreground">Dormitorios</div>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Bath className="h-5 w-5 text-muted-foreground" />
                <div>
                  <div className="text-sm font-medium">{propertyData.bathrooms}</div>
                  <div className="text-xs text-muted-foreground">Baños</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Link2 className="h-5 w-5 text-muted-foreground" />
                <div>
                  <div className="text-sm font-medium">{propertyData.platform}</div>
                  <div className="text-xs text-muted-foreground">Plataforma</div>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t">
              <h3 className="text-sm font-semibold mb-2">Descripción</h3>
              <p className="text-sm text-muted-foreground">{propertyData.description}</p>
            </div>

            <div className="pt-2">
              <a
                href={propertyData.platformUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
              >
                <ExternalLink className="h-4 w-4" />
                Ver en {propertyData.platform}
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Owner Information */}
        <Card>
          <CardHeader>
            <CardTitle>Propietario</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16">
                <AvatarImage src={`https://avatar.vercel.sh/${propertyData.owner.email}`} alt={propertyData.owner.name} />
                <AvatarFallback>
                  {propertyData.owner.name
                    .split(" ")
                    .map((chunk: string) => chunk[0])
                    .join("")}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 space-y-1">
                <h3 className="break-words text-lg font-semibold">{propertyData.owner.name}</h3>
                <p className="truncate text-sm text-muted-foreground" title={propertyData.owner.email}>{propertyData.owner.email}</p>
                {propertyData.owner.phone && (
                  <p className="truncate text-sm text-muted-foreground">{propertyData.owner.phone}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </ScrollArea>
  )
}
